import { Badge, Box, Button, Card, Container, Group, Loader, SimpleGrid, Stack, Table, Text, Title, useMantineColorScheme, Alert } from "@mantine/core";
import { useNavigate, useParams } from "react-router-dom";
import { useMatchQuery } from "generated/graphql";
import MatchHeaderCard from "./MatchHeaderCard";
import MapIcon from "components/Graphics/MapIcon";

/**
 * MatchDetails
 *
 * Displays a full-page view of a single match, including summary, results, and
 * player scoreboards. Uses the Match GraphQL query to fetch match data.
 * Route: /matches/:matchId
 */
export default function MatchDetails() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const { data, loading, error } = useMatchQuery({
    variables: { id: matchId ?? "" },
    skip: !matchId,
  });

  const match = data?.match;

  if (loading) {
    return (
      <Container size="lg" p="md">
        <Group justify="center" p="xl">
          <Loader size="lg" />
          <Text>Loading match details...</Text>
        </Group>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" p="md">
        <Alert color="red" title="Error loading match">
          {error.message}
        </Alert>
        <Button variant="light" onClick={() => navigate(-1)} mt="md">
          Go Back
        </Button>
      </Container>
    );
  }

  if (!match) {
    return (
      <Container size="lg" p="md">
        <Alert color="yellow" title="Match not found">
          The requested match could not be found.
        </Alert>
        <Button variant="light" onClick={() => navigate(-1)} mt="md">
          Go Back
        </Button>
      </Container>
    );
  }

  // Get team names from lobby players
  const teamAPlayers = match.lobby?.players?.filter(p => p.team === "TEAM_A") ?? [];
  const teamBPlayers = match.lobby?.players?.filter(p => p.team === "TEAM_B") ?? [];

  const homeTeam = teamAPlayers.length > 0 ? "Team A" : "Home";
  const awayTeam = teamBPlayers.length > 0 ? "Team B" : "Away";

  // Build player stats for scoreboards
  const homeScoreboard = (match.playerStats ?? [])
    .filter((ps): ps is NonNullable<typeof ps> => ps !== null && ps.team === "TEAM_A")
    .map(ps => ({
      player: ps.user?.name ?? "Unknown",
      kills: ps.kills ?? 0,
      deaths: ps.deaths ?? 0,
      adr: ps.averageDamagePerRound ?? undefined,
    }));

  const awayScoreboard = (match.playerStats ?? [])
    .filter((ps): ps is NonNullable<typeof ps> => ps !== null && ps.team === "TEAM_B")
    .map(ps => ({
      player: ps.user?.name ?? "Unknown",
      kills: ps.kills ?? 0,
      deaths: ps.deaths ?? 0,
      adr: ps.averageDamagePerRound ?? undefined,
    }));

  // Determine match result
  const isCompleted = match.status === "Completed";
  const score = match.homeTeamScore !== null && match.awayTeamScore !== null
    ? `${match.homeTeamScore}-${match.awayTeamScore}`
    : undefined;

  let result: "W" | "L" | "D" | undefined = undefined;
  if (isCompleted && match.winningTeam) {
    result = match.winningTeam === "TEAM_A" ? "W" : "L";
  } else if (isCompleted && match.homeTeamScore === match.awayTeamScore) {
    result = "D";
  }

  // Prepare match data for header card
  const statusLower = match.status?.toLowerCase();
  const resolvedStatus: "completed" | "live" | "scheduled" =
    statusLower === "completed" ? "completed" :
    statusLower === "live" ? "live" : "scheduled";

  const matchHeaderData = {
    id: match.id,
    status: resolvedStatus,
    result,
    score,
    homeTeam,
    awayTeam,
    scheduledAt: match.scheduledTime ?? match.startTime ?? new Date().toISOString(),
    tournament: match.tier?.tierName ?? match.matchType ?? "Match",
    bestOf: 1, // Single game
  };

  return (
    <Container size="lg" p="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3} c={isDark ? 'gray.1' : 'dark.5'}>Match Details</Title>
          <Group gap="xs">
            <Button variant="light" onClick={() => navigate(-1)}>Back</Button>
          </Group>
        </Group>

        <MatchHeaderCard match={matchHeaderData} />

        {/* Player Scoreboards */}
        {(homeScoreboard.length > 0 || awayScoreboard.length > 0) && (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <ScoreboardCard title={`${homeTeam} Players`} rows={homeScoreboard} />
            <ScoreboardCard title={`${awayTeam} Players`} rows={awayScoreboard} />
          </SimpleGrid>
        )}

        {/* Map Info */}
        {match.map && (
          <Card withBorder radius="md" p="md">
            <Stack gap={8}>
              <Title order={5} c={isDark ? 'gray.1' : 'dark.5'}>Map</Title>
              <Group gap={10} align="center">
                <MapIcon name={match.map as any} size={32} />
                <Text fw={600}>{match.map}</Text>
                {score && (
                  <Badge variant={isDark ? 'filled' : 'light'} color="blue">
                    {score}
                  </Badge>
                )}
              </Group>
            </Stack>
          </Card>
        )}

        {/* Server Info */}
        {match.server && (
          <Card withBorder radius="md" p="md">
            <Stack gap={8}>
              <Title order={5} c={isDark ? 'gray.1' : 'dark.5'}>Server</Title>
              <Group gap="md">
                <Text size="sm">
                  <Text span fw={600}>Server:</Text> {match.server.name ?? "Unknown"}
                </Text>
                {match.server.connectString && (
                  <Text size="sm">
                    <Text span fw={600}>Connect:</Text> {match.server.connectString}
                  </Text>
                )}
              </Group>
            </Stack>
          </Card>
        )}

        {/* Demo URL */}
        {match.demoUrl && (
          <Card withBorder radius="md" p="md">
            <Stack gap={8}>
              <Title order={5} c={isDark ? 'gray.1' : 'dark.5'}>Demo</Title>
              <Button
                component="a"
                href={match.demoUrl}
                target="_blank"
                variant="light"
                size="sm"
              >
                Download Demo
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}

function ScoreboardCard({ title, rows }: { title: string; rows: ReadonlyArray<{ player: string; kills: number; deaths: number; adr?: number }> }) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  if (rows.length === 0) {
    return (
      <Card withBorder radius="md" p="md">
        <Stack gap="sm">
          <Text fw={700} size="sm" c={isDark ? 'gray.0' : 'dark.7'}>{title}</Text>
          <Text size="sm" c="dimmed">No player stats available</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Text fw={700} size="sm" c={isDark ? 'gray.0' : 'dark.7'}>{title}</Text>
        <Table striped withTableBorder horizontalSpacing="md" verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Player</Table.Th>
              <Table.Th>K</Table.Th>
              <Table.Th>D</Table.Th>
              <Table.Th>ADR</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.player}>
                <Table.Td>{r.player}</Table.Td>
                <Table.Td>{r.kills}</Table.Td>
                <Table.Td>{r.deaths}</Table.Td>
                <Table.Td>{typeof r.adr === 'number' ? Math.round(r.adr) : '-'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
